import type { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { now } from '@seen/core/utils'
import { Button } from '@seen/ui'
import type { JSX } from 'react'
import { z } from 'zod'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { RequiredAnswersDialog } from '#src/components/rich-text-editor/required/RequiredAnswersDialog.tsx'
import { useHeldAnswers } from '#src/components/rich-text-editor/required/useHeldAnswers.ts'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useForm } from '#src/hooks/useForm.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { trimHTML } from '#src/utils/trimHTML.ts'

const formSchema = z.object({
  message: z
    .string()
    .transform(trimHTML)
    .refine((v) => v.length > 0, { message: 'Message required' }),
})

export function CommentEditor({
  comment,
  onClose,
}: {
  comment: CommentCommunication
  onClose: () => void
}): JSX.Element {
  const form = useForm({
    schema: formSchema,
    defaultValues: {
      message: comment.message,
    },
  })

  const { mutateAsync: mutateComment } =
    usePatchMutation<CommentCommunication>()
  // Saving an edit records its answers again under the same comment, and
  // retracts those it no longer holds.
  const held = useHeldAnswers({ surface: 'comment' })

  const handleSubmit = form.handleSubmit(async (values) => {
    if (!values.message.trim()) {
      return
    }
    const prepared = await held.prepare(values.message)
    if (!prepared) {
      return
    }

    /* The editor closes straight away, which drops a per-call `onSuccess`,
     * so the answers are recorded from the save's promise. A failed save is
     * reported by the mutation itself. */
    mutateComment({
      instance: comment,
      mutateFn: (c) => {
        c.message = prepared.message
        c.editedDate = now()
      },
    }).then(
      () =>
        held.record(prepared.answers, {
          noteKey: comment.reference.reference ?? '',
          subject: comment.subject,
        }),
      () => {}
    )

    onClose()
  })

  return (
    <Form onSubmit={handleSubmit} className="flex flex-col gap-1.5">
      <FormField
        control={form.control}
        name="message"
        render={({ field }) => (
          <RichTextEditor
            placeholder="Leave a comment…"
            variant="unstyled"
            related={[comment.reference]}
            editorRef={held.editorRef}
            observationRecordStatus="deferred"
            {...field}
          />
        )}
      />
      <div className="flex justify-end gap-2">
        <Button size="icon" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="icon" variant="outline">
          Save
        </Button>
      </div>
      <RequiredAnswersDialog {...held.dialog} />
    </Form>
  )
}
